/**
 * DMPaywallModal · iter172c (2026-02-27)
 *
 * Erscheint wenn Backend `/messages/social/threads` mit
 * 403 DM_GATE_CLOSED antwortet. Zeigt dem Sender:
 *   - Info: „Creator empfängt keine offenen DMs"
 *   - Aktive Abos beim Creator (mit Nachrichten-Kontingent im Tier)
 *   - Zum Kauf angebotene Nachrichten-Pakete
 *   - Buttons: Abo abschließen · Paket sofort kaufen (Stripe-Checkout).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, MessageCircle, Sparkles, ExternalLink, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export default function DMPaywallModal({ open, creatorId, creatorName, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(null); // { free_for_all, tier_quotas, packages }
  const [buying, setBuying] = useState(null); // package index being purchased
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !creatorId) return;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await api.get(`/creators/${creatorId}/messaging-info`);
        setCfg(data || {});
      } catch (e) {
        setError(formatError(e) || "Nachrichten-Optionen konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, creatorId]);

  const buyPackage = async (idx) => {
    setBuying(idx);
    setError("");
    try {
      const { data } = await api.post("/messaging/packages/checkout", {
        creator_id: creatorId,
        package_index: idx,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      throw new Error("Kein Checkout-Link erhalten.");
    } catch (e) {
      setError(formatError(e) || "Checkout fehlgeschlagen.");
      setBuying(null);
    }
  };

  const goToSubscribe = () => {
    onClose && onClose();
    // creator handle nicht immer bekannt → fallback: creator-Profil öffnen
    navigate(`/creators/${creatorId}#subscribe`);
  };

  if (!open) return null;

  const packages = cfg?.packages || [];
  const tierQuotas = cfg?.tier_quotas || {};
  const hasTierQuotas = ["standard", "premium", "vip"].some((k) => Number(tierQuotas[k]) > 0);

  return (
    <div
      data-testid="dm-paywall-modal"
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <MessageCircle size={16} className="text-[#0A0A0A]" />
            <h2 className="text-sm font-bold text-[#0A0A0A]">{t("dm_paywall.send_dm")}</h2>
          </div>
          <button data-testid="dm-paywall-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#F5F5F5]"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-[#666] leading-relaxed">
            <strong>{creatorName || "Dieser Creator"}</strong> empfängt keine offenen Nachrichten.
            Um zu schreiben, benötigst du ein aktives Abo mit Nachrichten-Kontingent
            oder ein Nachrichten-Paket.
          </p>

          {/* iter172d · „Erste Nachricht gratis"-Info (falls Creator diese Option aktiv hat und User noch nie geschrieben) */}
          {cfg?.first_message_free && (
            <div
              data-testid="dm-paywall-first-free-info"
              className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-start gap-2 text-xs text-emerald-900"
            >
              <Sparkles size={13} className="text-emerald-700 mt-0.5 shrink-0" />
              <span>
                <strong>{t("dm_paywall.first_free_prefix")}</strong> Dieser Creator lässt Interessenten
                einmalig kostenlos schreiben. Falls du noch nie geschrieben hast, sollte deine
                Nachricht direkt durchgehen — falls nicht, hast du diese Chance bereits genutzt.
              </span>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="dm-paywall-error">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-6 text-xs text-[#999]">
              <Loader2 className="inline animate-spin" size={16} /> Lade…
            </div>
          ) : (
            <>
              {/* Abo-Route */}
              {hasTierQuotas && (
                <div className="rounded-xl border border-[#E5E5E5] p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={13} className="text-amber-600" />
                    <div className="text-xs font-bold text-[#0A0A0A]">{t("dm_paywall.start_subscription")}</div>
                  </div>
                  <ul className="text-[11px] text-[#666] space-y-1 mb-2">
                    {Number(tierQuotas.standard) > 0 && <li>Standard-Abo: {tierQuotas.standard} Nachrichten / 30 Tage</li>}
                    {Number(tierQuotas.premium) > 0 && <li>Premium-Abo: {tierQuotas.premium} Nachrichten / 30 Tage</li>}
                    {Number(tierQuotas.vip) > 0 && <li>VIP-Abo: {tierQuotas.vip} Nachrichten / 30 Tage</li>}
                  </ul>
                  <button
                    data-testid="dm-paywall-subscribe-btn"
                    onClick={goToSubscribe}
                    className="w-full h-9 rounded-full bg-[#0A0A0A] text-white text-xs font-medium hover:bg-[#222] transition inline-flex items-center justify-center gap-1"
                  >
                    Abo-Optionen ansehen <ExternalLink size={11} />
                  </button>
                </div>
              )}

              {/* Nachrichten-Pakete */}
              {packages.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#999]">
                    Nachrichten-Pakete (Einmalkauf)
                  </div>
                  {packages.map((p, idx) => (
                    <div
                      key={idx}
                      data-testid={`dm-paywall-package-${idx}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-[#E5E5E5] p-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-[#0A0A0A] truncate">
                          {p.label || `${p.messages} Nachrichten`}
                        </div>
                        <div className="text-[10px] text-[#666]">
                          {p.messages} Nachrichten · gültig bis verbraucht
                        </div>
                      </div>
                      <button
                        data-testid={`dm-paywall-buy-${idx}`}
                        onClick={() => buyPackage(idx)}
                        disabled={buying !== null}
                        className="shrink-0 h-8 px-3 rounded-full bg-[#0A0A0A] text-white text-xs font-bold hover:bg-[#222] transition disabled:opacity-50 inline-flex items-center gap-1"
                      >
                        {buying === idx ? <Loader2 size={12} className="animate-spin" /> : null}
                        {Number(p.price_eur).toFixed(2).replace(".", ",")} €
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!hasTierQuotas && packages.length === 0 && (
                <div className="text-xs text-[#999] text-center py-6" data-testid="dm-paywall-empty">
                  Dieser Creator bietet aktuell keinen kostenpflichtigen Nachrichten-Zugang an.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
